import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import AccessPage from "../Shared/accessPage";
import ReactApexChart from 'react-apexcharts';

const SimulationDetails = () => {
  const [simulation, setSimulation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const { simulationId } = useParams();
  const auth = useSelector((state) => state?.auth?.user);
  const vendorId = sessionStorage.getItem('vendorId');
  const vendorName = sessionStorage.getItem('vendorName');

  // Mock simulation data
  const mockSimulationData = {
    id: simulationId || "1",
    date: "Jul 30, 2025",
    asset: "login.api.com",
    attackType: "SSH Brute Force",
    status: "Success",
    duration: "1.5 min",
    description: "Simulated SSH brute force attack against authentication server",
    
    // Attack Details
    attackVector: {
      method: "SSH Brute Force",
      targetPort: 22,
      attempts: 1000,
      credentials: ["admin", "root", "user"],
      timeWindow: "5 minutes"
    },
    
    // Results
    results: {
      firewallResponse: "No alert detected",
      exploitOutcome: "Simulated credential compromise",
      vulnerabilitiesFound: [
        "Weak password policy",
        "No rate limiting",
        "Default SSH configuration"
      ],
      riskScore: 8.5
    },
    
    // Network Details
    networkDetails: {
      sourceIP: "192.168.1.100",
      targetIP: "10.0.0.50",
      protocol: "SSH",
      port: 22,
      packetsSent: 1500,
      packetsReceived: 1200
    },
    
    // Timeline
    timeline: [
      {
        time: "00:00:00",
        event: "Simulation started",
        details: "Initializing SSH connection"
      },
      {
        time: "00:00:15",
        event: "Connection established",
        details: "SSH handshake completed"
      },
      {
        time: "00:00:30",
        event: "Brute force initiated",
        details: "Testing common credentials"
      },
      {
        time: "00:01:15",
        event: "Vulnerability detected",
        details: "Weak password policy identified"
      },
      {
        time: "00:01:30",
        event: "Simulation completed",
        details: "Results compiled and analyzed"
      }
    ],
    
    // Remediation Suggestions
    remediation: [
      {
        priority: "High",
        title: "Implement Rate Limiting",
        description: "Add rate limiting to prevent brute force attacks",
        impact: "Prevents automated credential attacks",
        effort: "Medium"
      },
      {
        priority: "High",
        title: "Enable MFA on SSH",
        description: "Implement multi-factor authentication for SSH access",
        impact: "Significantly reduces credential compromise risk",
        effort: "High"
      },
      {
        priority: "Medium",
        title: "Strengthen Password Policy",
        description: "Enforce complex password requirements",
        impact: "Reduces weak password vulnerabilities",
        effort: "Low"
      },
      {
        priority: "Medium",
        title: "Change Default SSH Configuration",
        description: "Disable root login and change default port",
        impact: "Reduces attack surface",
        effort: "Low"
      }
    ]
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setSimulation(mockSimulationData);
      setIsLoading(false);
    }, 1000);
  }, [simulationId]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "danger";
      case "Blocked":
        return "success";
      case "Error":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Success":
        return "fa-check-circle";
      case "Blocked":
        return "fa-shield-alt";
      case "Error":
        return "fa-exclamation-triangle";
      default:
        return "fa-question-circle";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "danger";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "secondary";
    }
  };

  const chartOptions = {
    chart: {
      type: 'radialBar',
    },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: {
          margin: 15,
          size: '70%',
        },
        track: {
          background: '#e7e7e7',
          strokeWidth: '97%',
          margin: 0,
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            offsetY: 10,
            color: '#111',
            fontSize: '22px',
            show: true,
          }
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        shadeIntensity: 0.5,
        gradientToColors: ['#ABE5A1'],
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    stroke: {
      lineCap: 'round'
    },
    labels: ['Risk Score'],
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading simulation details...</p>
        </div>
      </div>
    );
  }

  if (!simulation) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <h4>Simulation not found</h4>
          <Link to="/red-teaming" className="btn btn-primary">
            Back to Red Teaming
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid">
        <AccessPage></AccessPage>
        <div className="block-header">
          <div className="row clearfix">
            <div className="col-md-6 col-sm-12">
              <h1 className="welcome-text">Simulation Details</h1>
              <nav aria-label="breadcrumb" className="mt-1">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    {vendorId ? (
                      <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                        Vendor Assessment
                      </Link>
                    ) : (
                      <Link to="/" className="welcome-textSmall">Home</Link>
                    )}
                  </li>
                  {vendorName && (
                    <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                      <Link to={`/dashboard/vendor/${vendorId}`}>{vendorName}</Link>
                    </li>
                  )}
                  <li className="breadcrumb-item">
                    <Link to="/red-teaming" className="welcome-textSmall">Red Teaming</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                    Simulation {simulation.id}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="col-md-6 col-sm-12 hidden-xs d-flex justify-content-end align-items-center">
              <Link to={`/red-teaming${vendorId ? '/vendor/' + vendorId : ''}`} className="btn btn-secondary mr-2">
                <i className="fa fa-arrow-left"></i> Back
              </Link>
              <button className="btn btn-primary">
                <i className="fa fa-download"></i> Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Simulation Header */}
        <div className="card">
          <div className="body">
            <div className="row">
              <div className="col-md-8">
                <h4>{simulation.asset}</h4>
                <p className="text-muted mb-0">{simulation.description}</p>
              </div>
              <div className="col-md-4 text-right">
                <span className={`badge badge-${getStatusColor(simulation.status)} badge-lg`}>
                  <i className={`fa ${getStatusIcon(simulation.status)} mr-1`}></i>
                  {simulation.status}
                </span>
                <p className="text-muted mt-2 mb-0">
                  <i className="fa fa-clock mr-1"></i>
                  {simulation.date} • {simulation.duration}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-3" role="tablist">
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
              style={{ cursor: "pointer" }}
            >
              Overview
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "attack" ? "active" : ""}`}
              onClick={() => setActiveTab("attack")}
              style={{ cursor: "pointer" }}
            >
              Attack Details
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "timeline" ? "active" : ""}`}
              onClick={() => setActiveTab("timeline")}
              style={{ cursor: "pointer" }}
            >
              Timeline
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "remediation" ? "active" : ""}`}
              onClick={() => setActiveTab("remediation")}
              style={{ cursor: "pointer" }}
            >
              Remediation
            </a>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="row clearfix">
              <div className="col-lg-4 col-md-6">
                <div className="card">
                  <div className="body">
                    <h6>Risk Score</h6>
                    <ReactApexChart
                      options={chartOptions}
                      series={[simulation.results.riskScore * 10]}
                      type="radialBar"
                      height={200}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-8 col-md-6">
                <div className="card">
                  <div className="body">
                    <h6>Vulnerabilities Found</h6>
                    <ul className="list-unstyled">
                      {simulation.results.vulnerabilitiesFound.map((vuln, index) => (
                        <li key={index} className="mb-2">
                          <i className="fa fa-exclamation-triangle text-warning mr-2"></i>
                          {vuln}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attack Details Tab */}
          {activeTab === "attack" && (
            <div className="row clearfix">
              <div className="col-md-6">
                <div className="card">
                  <div className="body">
                    <h6>Attack Vector</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Method:</strong></td>
                          <td>{simulation.attackVector.method}</td>
                        </tr>
                        <tr>
                          <td><strong>Target Port:</strong></td>
                          <td>{simulation.attackVector.targetPort}</td>
                        </tr>
                        <tr>
                          <td><strong>Attempts:</strong></td>
                          <td>{simulation.attackVector.attempts}</td>
                        </tr>
                        <tr>
                          <td><strong>Time Window:</strong></td>
                          <td>{simulation.attackVector.timeWindow}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="body">
                    <h6>Network Details</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Source IP:</strong></td>
                          <td>{simulation.networkDetails.sourceIP}</td>
                        </tr>
                        <tr>
                          <td><strong>Target IP:</strong></td>
                          <td>{simulation.networkDetails.targetIP}</td>
                        </tr>
                        <tr>
                          <td><strong>Protocol:</strong></td>
                          <td>{simulation.networkDetails.protocol}</td>
                        </tr>
                        <tr>
                          <td><strong>Packets Sent:</strong></td>
                          <td>{simulation.networkDetails.packetsSent}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="card">
                  <div className="body">
                    <h6>Results</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Firewall Response:</strong> {simulation.results.firewallResponse}</p>
                        <p><strong>Exploit Outcome:</strong> {simulation.results.exploitOutcome}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Risk Score:</strong> {simulation.results.riskScore}/10</p>
                        <p><strong>Status:</strong> 
                          <span className={`badge badge-${getStatusColor(simulation.status)} ml-2`}>
                            {simulation.status}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === "timeline" && (
            <div className="card">
              <div className="body">
                <h6>Simulation Timeline</h6>
                <div className="timeline">
                  {simulation.timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker bg-primary"></div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between">
                          <h6 className="timeline-title">{event.event}</h6>
                          <small className="text-muted">{event.time}</small>
                        </div>
                        <p className="timeline-text mb-0">{event.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Remediation Tab */}
          {activeTab === "remediation" && (
            <div className="card">
              <div className="body">
                <h6>Remediation Suggestions</h6>
                <div className="row">
                  {simulation.remediation.map((item, index) => (
                    <div key={index} className="col-md-6 mb-3">
                      <div className="card border-left-4" style={{ borderLeftColor: item.priority === "High" ? "#dc3545" : item.priority === "Medium" ? "#ffc107" : "#17a2b8" }}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0">{item.title}</h6>
                            <span className={`badge badge-${getPriorityColor(item.priority)}`}>
                              {item.priority}
                            </span>
                          </div>
                          <p className="card-text">{item.description}</p>
                          <div className="row">
                            <div className="col-6">
                              <small className="text-muted">
                                <strong>Impact:</strong> {item.impact}
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted">
                                <strong>Effort:</strong> {item.effort}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SimulationDetails; 